import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Layout, Divider, Spin, Table, Input, Button, message, Collapse } from "antd";
import { FileTextOutlined, SearchOutlined, UserOutlined, DollarOutlined, AppstoreOutlined } from "@ant-design/icons";
import CommonDivWidget from "components/CommonDivWidget/CommonDivWidget";
import ClubBoxWidget from "components/ClubBoxWidget/ClubBoxWidget";
import { apiGetReportNames, apiRunReport } from "services/dashboardService";
import PrintableReport from "./PrintableReport";
import ReactToPrint from "react-to-print";
import './CustomReportsPage.css';

const { Content, Sider } = Layout;

function CustomReportsPage() {
    const userDataStore = useSelector((x) => x.userDataStore);
    const appDataStore = useSelector((x) => x.appDataStore);

    const [isBusy, setIsBusy] = useState(false);
    const [reportNamesList, setReportNamesList] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [inputs, setInputs] = useState({});
    const [results, setResults] = useState([]);
    const [printHeader, setPrintHeader] = useState("");
    const [searchKey, setSearchKey] = useState("");
    const printComponentRef = useRef(null);

    const getReportNames = useCallback(() => {
        const params = {
            "token": userDataStore.token,
            "club_id": appDataStore.club.id,
            "registration_id": appDataStore.curRegistration.ID,
            "user_id": userDataStore.user.ID,
        };

        setIsBusy(true);
        apiGetReportNames(params)
            .then((res) => {
                if (res && res.report_names) {
                    setReportNamesList(res.report_names);
                } else {
                    setReportNamesList([]);
                }
                setIsBusy(false);
            })
            .catch((err) => {
                message.error(err);
                setIsBusy(false);
            });
    }, [appDataStore.club, appDataStore.curRegistration, userDataStore.token, userDataStore.user]);

    useEffect(() => {
        if (appDataStore.club && appDataStore.curRegistration) {
            getReportNames();
        }
    }, [appDataStore.club, appDataStore.curRegistration, getReportNames]); // getReportNames was added as a dependency

    useEffect(() => {
        // Set the print header to include the logo, centered.
        const headerHtml = `
      <div style="text-align: center;">
        <img src="/assets/images/logo-large.png" alt="Company Logo" style="max-width: 250px; margin-bottom: 20px;" />
      </div>`;
        setPrintHeader(headerHtml);
    }, []);

    const handleReportClick = (report) => {
        console.log('========== REPORT CLICK START ==========');
        console.log('Report clicked:', report);
        console.log('Current selectedReport:', selectedReport);
        console.log('Current results:', results);
        console.log('Current inputs:', inputs);
        console.log('Current baseColumns:', baseColumns);

        try {
            console.log('About to setSelectedReport...');
            setSelectedReport(report);
            console.log('setSelectedReport completed');

            console.log('About to setInputs...');
            setInputs({});
            console.log('setInputs completed');

            console.log('About to setResults...');
            setResults([]);
            console.log('setResults completed');

            console.log('========== REPORT CLICK END ==========');
        } catch (error) {
            console.error('Error in handleReportClick:', error);
            console.error('Error stack:', error.stack);
            message.error('Error selecting report: ' + error.message);
        }
    };

    // Group reports by category based on display_group integer
    const categorizedReports = useMemo(() => {
        const categories = {
            1: { name: 'Registration Reports', reports: [] },
            2: { name: 'Financial Reports', reports: [] },
            3: { name: 'Other Reports', reports: [] }
        };

        reportNamesList.forEach((report) => {
            const displayGroup = report.display_group || 3; // Default to 3 (Other Reports)
            if (categories[displayGroup]) {
                categories[displayGroup].reports.push(report);
            } else {
                categories[3].reports.push(report); // Fallback to Other Reports
            }
        });

        return categories;
    }, [reportNamesList]);

    // Filter reports based on search key
    const filteredReports = useMemo(() => {
        if (!searchKey || searchKey.trim() === '') {
            return categorizedReports;
        }

        const filtered = {
            1: { name: 'Registration Reports', reports: [] },
            2: { name: 'Financial Reports', reports: [] },
            3: { name: 'Other Reports', reports: [] }
        };

        const searchLower = searchKey.toLowerCase();

        Object.keys(categorizedReports).forEach((key) => {
            filtered[key].reports = categorizedReports[key].reports.filter((report) =>
                report.report_name.toLowerCase().includes(searchLower)
            );
        });

        return filtered;
    }, [categorizedReports, searchKey]);

    const handleInputChange = (field, value) => {
        setInputs((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        if (!selectedReport) {
            message.warning("Please select a report first.");
            return;
        }

        const inputFields = selectedReport.input_names ? selectedReport.input_names.split(",") : [];

        // Always add registration_id first
        const params = new URLSearchParams();
        params.append("token", userDataStore.token);
        params.append("registration_id", appDataStore.curRegistration.ID);
        params.append("report_sp", selectedReport.report_sp);

        params.append("input1", appDataStore.curRegistration.ID);

        // Then add user inputs as input2, input3...
        for (let i = 1; i < inputFields.length; i++) {
            params.append(`input${i + 1}`, inputs[inputFields[i]]);
        }

        setIsBusy(true);
        apiRunReport(params)
            .then((res) => {
                if (res && res.results.length > 0) {
                    setResults(res.results);
                } else {
                    setResults([]);
                    message.info("No results found.");
                }
                setIsBusy(false);
            })
            .catch((err) => {
                message.error("Failed to fetch report data.");
                setIsBusy(false);
            });
    };


    const handleExport = () => {
        if (!results.length) {
            message.warning("No data to export.");
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(results);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Results");

        const today = new Date();
        const filename = `CustomReports_${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}.xlsx`;

        XLSX.writeFile(workbook, filename);
    };

    // Generate columns for the results table
    const baseColumns = useMemo(() => {
        if (results.length === 0) {
            return [];
        }

        return Object.keys(results[0]).map((key, idx, arr) => {
            // Find first non-null value to determine column type for sorting
            const firstNonNullValue = results.find(item => item[key] != null)?.[key];
            const isNumericColumn = typeof firstNonNullValue === 'number';

            return {
                title: key,
                dataIndex: key,
                key,
                sorter: (a, b) => {
                    const valA = a[key];
                    const valB = b[key];

                    // Keep nulls/undefined at the top
                    if (valA == null && valB == null) return 0;
                    if (valA == null) return -1;
                    if (valB == null) return 1;

                    if (isNumericColumn) {
                        return valA - valB;
                    }

                    // Case-insensitive string sort
                    return String(valA).localeCompare(String(valB), undefined, {
                        numeric: true,
                        sensitivity: "base",
                    });
                },
                ellipsis: true,
                // Set width for all but the last column to avoid drag issues
                width: idx < arr.length - 1 ? 150 : undefined,
            };
        });
    }, [results]);

    console.log('========== RENDER START ==========');
    console.log('Rendering CustomReportsPage');
    console.log('selectedReport:', selectedReport);
    console.log('results.length:', results.length);
    console.log('baseColumns.length:', baseColumns.length);
    console.log('isBusy:', isBusy);
    console.log('========== RENDER END ==========');

    return (
        <CommonDivWidget>
            <Spin spinning={isBusy}>
                <Layout style={{ background: "#fff", padding: 16 }}>
                    <ClubBoxWidget />
                    <Divider style={{ borderTop: "4px solid var(--mainColor)" }} />

                    <Layout>
                        <Sider width={300} style={{ background: "#f7f7f7", padding: 16, paddingTop: 16 }}>
                            <div style={{ marginBottom: 16 }}>
                                <Input
                                    placeholder="Search reports..."
                                    prefix={<SearchOutlined />}
                                    value={searchKey}
                                    onChange={(e) => setSearchKey(e.target.value)}
                                    allowClear
                                    style={{ borderRadius: 6 }}
                                />
                            </div>
                            <div className="report-results-container">
                                <Collapse
                                    defaultActiveKey={['1', '2', '3']}
                                    ghost
                                    expandIconPosition="right"
                                    style={{ background: 'transparent' }}
                                >
                                    {[1, 2, 3].map((categoryKey) => {
                                        const category = filteredReports[categoryKey];
                                        console.log(`Rendering category ${categoryKey}:`, category.name, 'reports:', category.reports.length);

                                        if (category.reports.length === 0 && searchKey) {
                                            return null; // Hide empty categories when searching
                                        }

                                        // Select icon based on category
                                        const categoryIcon = categoryKey === 1 ? <UserOutlined style={{ marginRight: 8 }} /> :
                                                           categoryKey === 2 ? <DollarOutlined style={{ marginRight: 8 }} /> :
                                                           <AppstoreOutlined style={{ marginRight: 8 }} />;

                                        console.log('About to render Collapse.Panel for category:', categoryKey);
                                        console.log('Collapse:', Collapse);
                                        console.log('Collapse.Panel:', Collapse.Panel);
                                        console.log('typeof Collapse.Panel:', typeof Collapse.Panel);

                                        return (
                                            <Collapse.Panel
                                                header={
                                                    <span style={{ fontWeight: 500, fontSize: 14 }}>
                                                        {categoryIcon}
                                                        {category.name}
                                                    </span>
                                                }
                                                key={categoryKey}
                                            >
                                                {category.reports.length === 0 ? (
                                                    <div style={{ padding: '8px 0', color: '#999', fontSize: 12 }}>
                                                        No reports found
                                                    </div>
                                                ) : (
                                                    category.reports.map((report) => (
                                                        <div
                                                            key={report.id || report.report_name}
                                                            onClick={() => handleReportClick(report)}
                                                            style={{
                                                                padding: '8px 12px',
                                                                cursor: 'pointer',
                                                                borderRadius: 4,
                                                                marginBottom: 4,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                backgroundColor: selectedReport?.report_name === report.report_name ? '#e6f7ff' : 'transparent',
                                                                border: selectedReport?.report_name === report.report_name ? '1px solid #1890ff' : '1px solid transparent',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (selectedReport?.report_name !== report.report_name) {
                                                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (selectedReport?.report_name !== report.report_name) {
                                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                                }
                                                            }}
                                                        >
                                                            <FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                                            <span style={{ fontSize: 13 }}>{report.report_name}</span>
                                                        </div>
                                                    ))
                                                )}
                                            </Collapse.Panel>
                                        );
                                    })}
                                </Collapse>
                            </div>
                        </Sider>

                        <Content style={{ marginLeft: 16 }}>
                            {!selectedReport ? (
                                <div>
                                    <h2 className="report-title">Custom Reports</h2>
                                    <div
                                        style={{
                                            marginTop: 24,
                                            width: "100%",
                                            padding: "40px",
                                            textAlign: "center",
                                            backgroundColor: "#fff",
                                            borderRadius: "8px",
                                            border: "1px solid #eee",
                                        }}
                                    >
                                        <Table
                                            dataSource={Array.from({ length: 10 }, (_, i) => ({ key: i }))}
                                            columns={[
                                                { title: '', dataIndex: 'col1', key: 'col1', width: '20%' },
                                                { title: '', dataIndex: 'col2', key: 'col2', width: '20%' },
                                                { title: '', dataIndex: 'col3', key: 'col3', width: '20%' },
                                                { title: '', dataIndex: 'col4', key: 'col4', width: '20%' },
                                                { title: '', dataIndex: 'col5', key: 'col5', width: '20%' },
                                            ]}
                                            pagination={false}
                                            bordered
                                            rowClassName={(record, index) => index % 2 === 1 ? 'table-row-light' : ''}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    {console.log('========== SELECTED REPORT RENDER ==========', selectedReport)}
                                    <h2 className="report-title">Custom Reports</h2>

                                    {selectedReport.input_names && selectedReport.input_names.trim() !== '' && (
                                        <div style={{ marginBottom: 24, marginTop: 25 }}>
                                            {console.log('Rendering input section for:', selectedReport.report_name)}
                                            <h4>Inputs for {selectedReport.report_name}</h4>
                                            {selectedReport.input_names
                                                .split(",")
                                                .filter(name => name && name.trim())
                                                .slice(1)
                                                .map((inputName) => (
                                                    <Input
                                                        key={inputName.trim()}
                                                        placeholder={`Enter ${inputName.trim()}`}
                                                        value={inputs[inputName.trim()] || ""}
                                                        onChange={(e) => handleInputChange(inputName.trim(), e.target.value)}
                                                        style={{ marginBottom: 12, width: 300 }}
                                                    />
                                                ))}
                                            <div>
                                                <Button type="primary" onClick={handleSubmit}>
                                                    Submit
                                                </Button>
                                                <Button onClick={handleExport} style={{ marginLeft: 10 }} disabled={!results.length}>
                                                    Export
                                                </Button>
                                                <ReactToPrint
                                                    trigger={() => (
                                                        <Button style={{ marginLeft: 10 }} disabled={!results.length}>
                                                            Print
                                                        </Button>
                                                    )}
                                                    content={() => printComponentRef.current}
                                                    documentTitle={selectedReport ? `${selectedReport.report_name} - Custom Report` : "Custom Report"}
                                                    pageStyle={`
                                                        @media print {
                                                            @page {
                                                                size: landscape;
                                                                margin: 20mm;
                                                            }
                                                            .ant-table {
                                                                font-size: 10px;
                                                            }
                                                            h2 {
                                                                text-align: center;
                                                                font-size: 18px;
                                                                margin-bottom: 20px;
                                                            }
                                                        }`}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {results?.length > 0 ? (
                                        <div
                                            style={{
                                                width: "100%",
                                                overflowX: "auto",
                                                padding: "12px",
                                                backgroundColor: "#fff",
                                                borderRadius: "8px",
                                                border: "1px solid #eee",
                                                marginTop: 24,
                                            }}
                                        >
                                            {(() => {
                                                console.log('========== TABLE RENDER ==========');
                                                console.log('About to render Table with results');
                                                console.log('results:', results);
                                                console.log('baseColumns:', baseColumns);
                                                console.log('baseColumns type:', typeof baseColumns);
                                                console.log('baseColumns is Array:', Array.isArray(baseColumns));
                                                if (Array.isArray(baseColumns)) {
                                                    baseColumns.forEach((col, idx) => {
                                                        console.log(`Column ${idx}:`, {
                                                            title: col.title,
                                                            dataIndex: col.dataIndex,
                                                            key: col.key,
                                                            render: typeof col.render,
                                                            sorter: typeof col.sorter
                                                        });
                                                    });
                                                }
                                                console.log('========== TABLE RENDER END ==========');
                                                return null;
                                            })()}
                                            <Table
                                                dataSource={results}
                                                columns={baseColumns}
                                                pagination={false}
                                                rowKey={(record, index) => index}
                                                scroll={{ x: 'max-content' }}
                                                bordered
                                                rowClassName={(record, index) => index % 2 === 1 ? 'table-row-light' : ''}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                marginTop: 24,
                                                width: "100%",
                                                padding: "40px",
                                                textAlign: "center",
                                                backgroundColor: "#fff",
                                                borderRadius: "8px",
                                                border: "1px solid #eee",
                                                color: "#999",
                                                fontSize: "16px",
                                            }}
                                        >
                                            No results found.
                                        </div>
                                    )}
                                </div>
                            )}
                        </Content>
                    </Layout>
                    {/* Hidden component for printing. */}
                    <div style={{ display: 'none' }}>
                        <PrintableReport
                            ref={printComponentRef}
                            results={results}
                            columns={baseColumns}
                            reportName={selectedReport?.report_name}
                            printHeader={printHeader}
                        />
                    </div>
                </Layout>
            </Spin>
        </CommonDivWidget>
    );
}

export default CustomReportsPage;
